import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/shared/lib/supabase'
import { Lock, CheckCircle } from 'lucide-react'

export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Signing in or refocusing a tab does not mean a password was changed.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') setError('This reset link has expired. Please request a new link.')
    })
    return () => {
      subscription.unsubscribe()
      if (redirectTimer.current) clearTimeout(redirectTimer.current)
    }
  }, [])

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !sessionData.session) {
        setError('This reset link has expired. Please request a new link.')
        return
      }
      const { error } = await supabase.auth.updateUser({
        password: password,
      })

      if (error) {
        setError(error.message)
      } else {
        setSuccess(true)
        // Redirect to login after 3 seconds
        redirectTimer.current = setTimeout(() => {
          navigate('/')
        }, 3000)
      }
    } catch  {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card w-full max-w-md p-8 text-center space-y-6">
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16 text-green-600" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Password Reset Successful
          </h1>
          <p className="text-slate-600">
            Your password has been successfully updated.
          </p>
          <p className="text-sm text-slate-500">
            Redirecting you to the login page...
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-orange w-full"
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass-card w-full max-w-md p-8 text-center space-y-6">
        <div className="flex justify-center">
          <Lock className="h-12 w-12 text-nzrail-orange" />
        </div>
        <h1 className="text-3xl font-semibold text-slate-900">
          Reset Your Password
        </h1>
        <p className="text-slate-600">
          Please enter your new password below.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-left">
            {error}
          </div>
        )}

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="text-left">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
              New Password
            </label>
            <input
              id="password"
              type="password"
              placeholder="Enter new password"
              className="form-input"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="text-left">
            <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-2">
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              type="password"
              placeholder="Confirm new password"
              className="form-input"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="text-left text-xs text-slate-500 space-y-1">
            <p>Password requirements:</p>
            <ul className="list-disc ml-4">
              <li>At least 8 characters long</li>
              <li>Mix of uppercase and lowercase letters recommended</li>
              <li>Include numbers and special characters for better security</li>
            </ul>
          </div>

          <button
            type="submit"
            className="btn-orange w-full"
            disabled={loading}
          >
            {loading ? 'Updating Password...' : 'Reset Password'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn-secondary w-full"
            disabled={loading}
          >
            Cancel
          </button>
        </form>

        <p className="text-xs text-slate-500 mt-6">
          If you didn't request this password reset, please contact support immediately.
        </p>
      </div>
    </div>
  )
}
